import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Environment, EnvironmentService } from '@galaxy/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { map } from 'rxjs/operators';

/*
WordpressContentService loads data from a WebsitePro site defined by the URL set in loadContent.
Usually the account that hosts this site is on partner VCOR, Account Group ID AG-NP378DRWL2 or AG-J7CT2Z8R.

The site uses two plugins to create ad metadata: Meta Box and MB Rest API.

To create, update, or remove a field from the metadata, edit the Meta Box plugin and locate `meta-box/inc/functions.php`.
At the bottom of this file, the metaboxes are defined and can be edited to your liking.
Additional documentation for field definition can be found at https://metabox.io/docs/define-fields/
 */

export interface WordpressContentItem {
  id: string;
  bgColor: string;
  bgImage?: string;
  titleHtml?: string;
  descTextHtml: string;
  primaryButtonLink: string;
  primaryButtonText: string;
}

@Injectable({ providedIn: 'root' })
export class WordpressContentService {
  contentSubject = new BehaviorSubject<WordpressContentItem[]>(null);

  constructor(
    private http: HttpClient,
    private environmentService: EnvironmentService,
  ) {}

  get content(): Observable<WordpressContentItem[]> {
    return this.contentSubject.asObservable();
  }

  loadContent(contentUrl?: string): void {
    const env = this.environmentService.getEnvironment();
    this.contentSubject.next(null);
    if (!contentUrl) {
      contentUrl = this.getProductAdsListUrl(env);
    }
    this.http
      .get<any[]>(contentUrl)
      .pipe(
        map((res) => {
          return res.map((contentData) => this.toContent(contentData));
        }),
      )
      .subscribe((ads) => this.contentSubject.next(ads));
  }

  private getProductAdsListUrl(env: Environment) {
    return `https://${
      env === Environment.PROD ? 'product-ads.websitepro.hosting' : 'product-ads.websitepro-staging.com'
    }/wp-json/wp/v2/posts?_embed=1&status=publish&categories=10`;
  }

  private toContent(data: any): WordpressContentItem {
    const item = {} as WordpressContentItem;
    if (data.title) {
      item.titleHtml = data.title.rendered;
    }
    if (data.content) {
      item.descTextHtml = data.content.rendered;
    }
    if (data.meta_box) {
      item.id = data.meta_box.id;
      item.bgColor = data.meta_box.color;
      item.primaryButtonText = data.meta_box.buttonText;
      item.primaryButtonLink = data.meta_box.buttonLink;
    }
    /* tslint:disable:no-string-literal */
    if (data._embedded['wp:featuredmedia'] && data._embedded['wp:featuredmedia'].length) {
      item.bgImage = data._embedded['wp:featuredmedia'][0].source_url;
    }
    return item;
  }
}
